import { isInternalPath } from 'lib/helpers/redirect-helpers';

/**
 * Parse a Next.js router query param into a finite number.
 *
 * Router params are typed `string | string[] | undefined`. Returns the parsed
 * number only when the param is a single numeric string; otherwise (array,
 * missing, or non-numeric) returns `undefined`.
 */
export const parseNumericQueryParam = (value: string | string[] | undefined): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Read a Next.js router query param as a single string.
 *
 * Router params are typed `string | string[] | undefined`. Returns the value
 * only when it's a single string; a repeated param (`string[]`) or a missing
 * one returns `undefined` rather than stringifying the array.
 */
export const parseStringQueryParam = (value: string | string[] | undefined): string | undefined => typeof value === 'string' ? value : undefined;

/**
 * Read a `from` query param naming the in-app path a page's Back returns to.
 * Only an app-internal path is accepted (see `isInternalPath`).
 */
export const parseFromPath = (value: string | string[] | undefined): string | undefined => {
  const from = parseStringQueryParam(value);
  return isInternalPath(from) ? from : undefined;
};

/** Appends a `from` query param to an in-app href, for its page's Back. */
export const withFromPath = (href: string, from: string | undefined): string => from ? `${href}${href.includes('?') ? '&' : '?'}from=${encodeURIComponent(from)}` : href;